/*
 * premierstreets
 * version 4.5
 * http://www.premierstreets.com
 *
 * Copyright (c) 2021 premier streets
 * Author premier streets <support@premierstreets.com>
 * Licensed under the MIT license.
 */
import { NgModule } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SwiperModule } from 'ngx-swiper-wrapper';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatRippleModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSliderModule } from '@angular/material/slider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatStepperModule } from '@angular/material/stepper';
import { MatNativeDateModule } from '@angular/material/core';
import { VendorShareDialog } from '../pages/vendor-detail/vendor-share/vendor-share-popup/vendor-share-popup.component';
import { VendorEmailShareDialog } from '../pages/vendor-detail/vendor-share/vendor-email-share/vendor-email-share.component';
import { WelcomeModalComponent } from '../pages/home/welcome-modal/welcome-modal.component';


// component
import { SpinnerComponent } from './spinner/spinner.component';
import { BannersComponent } from './components/banners/banners.component';
import {
  PERFECT_SCROLLBAR_CONFIG,
  PerfectScrollbarConfigInterface
} from 'ngx-perfect-scrollbar';
import { PipesModule } from './pipes/pipes.module';
import { ProductCompareSandbox } from '../../core/product-compare/product-compare.sandbox';
import { ProductCompareService } from '../../core/product-compare/product-compare.service';
import { Effect, EffectsModule } from '@ngrx/effects';
import { ProductCompareEffect } from '../../core/product-compare/effects/product-compare.effects';
import { ProductCompareComponent } from './components/product-compare/product-compare.component';
import { VendorsCarouselComponent } from './components/vendors-carousel/vendors-carousel.component';
import { VendorsCarouselBannerComponent } from './components/vendors-carousel-banner/vendors-carousel-banner.component';
//import { EntertainmentCarouselBannerComponent } from './components/entertainment-carousel-banner/entertainment-carousel-banner.component';
import { TabComponent } from './components/detail-tabs/tab.component';
import { TabsComponent } from './components/detail-tabs/tabs.component';
import { MediaDirective } from '../pages/home/media-component/media.component';
import { VendorReviewComponent } from '../pages/products/product/vendor-review/vendor-review.component';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SocialChatModule } from '../pages/social-chat/social-chat.module';
import { VendorShareComponent } from '../pages/vendor-detail/vendor-share/vendor-share.component';
import { VideoModalComponent } from '../pages/home/video-modal/video-modal.component';
import { SafePipe } from '../pages/home/safe.pipe';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { VendorEmailChatShareDialog } from '../pages/vendor-detail/vendor-share/vendor-email-chat-share/vendor-email-chat-share.component';
import { ProductPrevieModalComponent } from './product-preview-modal/product-preview-modal.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  wheelPropagation: true,
  suppressScrollX: true
};

@NgModule({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    SwiperModule,
    FlexLayoutModule,
    MatAutocompleteModule,
    MatButtonModule,
    NgbModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    PipesModule,
    MatStepperModule,
    EffectsModule.forFeature([ProductCompareEffect]),
    TranslateModule.forChild(),
    FormsModule,
    SocialChatModule
  ],
  exports: [
    SpinnerComponent,
    RouterModule,
    SwiperModule,
    FlexLayoutModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatStepperModule,
    BannersComponent,
    VendorsCarouselComponent,
    VendorsCarouselBannerComponent,
    // EntertainmentCarouselBannerComponent,
    TabComponent,
    TabsComponent,
    MediaDirective,
    VendorReviewComponent,
    VendorShareComponent,
    SafePipe,
    VendorShareDialog,
    VendorEmailShareDialog,
    ClickOutsideDirective,
    VendorEmailChatShareDialog,
    NgbModule
  ],
  declarations: [SpinnerComponent, BannersComponent, VendorsCarouselComponent, VendorsCarouselBannerComponent, TabComponent, TabsComponent, MediaDirective, VendorReviewComponent, VendorShareComponent,
    VideoModalComponent, WelcomeModalComponent, SafePipe, ProductPrevieModalComponent,
    VendorShareDialog, VendorEmailChatShareDialog,
    VendorEmailShareDialog, ClickOutsideDirective],
  entryComponents: [
    VideoModalComponent, ProductPrevieModalComponent, WelcomeModalComponent, VendorShareDialog, VendorEmailShareDialog, VendorShareComponent, VendorEmailChatShareDialog,
  ],
  providers: [
    ProductCompareSandbox,
    ProductCompareService,
    DatePipe,
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
    }
  ]
})
export class SharedModule { }
